/**
 * Leaguemate Scouting — S3.5 win-now ↔ rebuild (pure, no I/O).
 *
 * Two corroborating signals (§7c — NOT avg age, which the audit killed):
 *   - INTENT: net_future_picks = future picks (season ≥ current) acquired − sent
 *     from /traded_picks. Per-league-normalized metric also surfaced (§7c carry).
 *   - STATE:  youth-tilt = youth_share − vet_share (% roster age ≤23 minus ≥29),
 *     pooled across the tx-set rosters (reused from S2, no refetch).
 *
 * Label confidently ONLY when intent + state AGREE. When they diverge (e.g.
 * kodypetey: spends picks yet young roster) → "transitional", don't force a pole.
 */

import {
  REBUILD_NET_MIN,
  WINNOW_NET_MAX,
  YOUTH_AGE_MAX,
  VET_AGE_MIN,
  YOUTH_TILT_STRONG,
  WINNOW_TILT_MAX,
  REBUILD_INTENT_MIN_TX_LEAGUES,
} from "./scoutConfig.js";

/** net_future_picks over the tx set, from /traded_picks rows. */
export function computeNetFuturePicks(pickRows, { currentSeason } = {}) {
  const cur = Number(currentSeason) || 0;
  let picksAcquired = 0;
  let picksSent = 0;
  for (const lg of pickRows || []) {
    const rid = lg.roster_id;
    for (const tp of lg.tradedPicks || []) {
      if (Number(tp.season) < cur) continue; // future picks only
      if (tp.owner_id === rid && tp.roster_id !== rid) picksAcquired++;
      else if (tp.roster_id === rid && tp.owner_id !== rid) picksSent++;
    }
  }
  const netFuturePicks = picksAcquired - picksSent;
  const leagueCount = (pickRows || []).length || 1;
  return {
    picksAcquired,
    picksSent,
    netFuturePicks, // raw over the ≤6 tx set — same scale as the audit's tx-set netPicks
    netPerLeague: Math.round((netFuturePicks / leagueCount) * 100) / 100, // fan-out-fair (re-fit bands here, §7c)
    pickLeagueCount: leagueCount,
  };
}

/** youth-tilt over the pooled tx-set rosters. */
export function computeYouthTilt(rosterRows, playersMap = {}) {
  let total = 0;
  let youth = 0;
  let vet = 0;
  for (const lg of rosterRows || []) {
    for (const pid of lg.players || []) {
      const age = (playersMap[pid] || {}).age;
      if (age == null) continue;
      total++;
      if (age <= YOUTH_AGE_MAX) youth++;
      else if (age >= VET_AGE_MIN) vet++;
    }
  }
  const youthShare = total ? Math.round((1000 * youth) / total) / 10 : 0;
  const vetShare = total ? Math.round((1000 * vet) / total) / 10 : 0;
  return { youthShare, vetShare, youthTilt: Math.round((youthShare - vetShare) * 10) / 10, ageSample: total };
}

/**
 * Combined win-now/rebuild read. `rosterRows` and `pickRows` are the tx-set
 * rows (rosterRows carry players[] from S2; pickRows carry tradedPicks).
 */
export function computeRebuild(pickRows, rosterRows, playersMap = {}, { currentSeason } = {}) {
  const picks = computeNetFuturePicks(pickRows, { currentSeason });
  const age = computeYouthTilt(rosterRows, playersMap);

  // Pick-based INTENT is only reliable over ≥2 tx leagues (S3.5 calibration).
  // When thin, suppress the pick intent and lean on roster youth-tilt (state).
  const intentReliable = picks.pickLeagueCount >= REBUILD_INTENT_MIN_TX_LEAGUES;
  const intent = !intentReliable
    ? "balanced"
    : picks.netFuturePicks >= REBUILD_NET_MIN ? "rebuild"
    : picks.netFuturePicks <= WINNOW_NET_MAX ? "win-now"
    : "balanced";
  const state =
    age.youthTilt > YOUTH_TILT_STRONG ? "young"
    : age.youthTilt < WINNOW_TILT_MAX ? "old"
    : "neutral";

  let label, confidence;
  if (intent === "rebuild" && state === "young") { label = "rebuild"; confidence = "high"; }
  else if (intent === "win-now" && state === "old") { label = "win-now"; confidence = "high"; }
  else if (intent === "rebuild" && state === "old") { label = "transitional"; confidence = "medium"; } // conflict
  else if (intent === "win-now" && state === "young") { label = "transitional"; confidence = "medium"; } // kodypetey
  else if (intent !== "balanced" && state === "neutral") { label = intent; confidence = "low"; } // intent leads
  else if (intent === "balanced" && state !== "neutral") { label = state === "young" ? "rebuild" : "win-now"; confidence = "low"; } // state only (or thin picks)
  else { label = "balanced"; confidence = "low"; }

  // Note for the UI when the read rests on roster age, not pick intent.
  const note = !intentReliable && state !== "neutral"
    ? "thin pick data; based on roster age"
    : null;

  return { label, confidence, intent, intentReliable, note, state, ...picks, ...age };
}
